import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type Notification,
  notificationsService,
} from '@/services/notificationsService'

export function useNotifications() {
  const queryClient = useQueryClient()

  const {
    data: notifications = [],
    isLoading,
    error,
  } = useQuery<Notification[]>({
    queryKey: ['notifications'],
    queryFn: () => notificationsService.getNotifications(),
    refetchInterval: 30000, // Refetch every 30 seconds
    refetchOnWindowFocus: true, // Refetch when window regains focus
    staleTime: 0, // Always consider data stale so invalidation causes immediate refetch
  })

  const { data: unreadCount = 0 } = useQuery<number>({
    queryKey: ['notifications', 'unreadCount'],
    queryFn: async () => {
      const count = await notificationsService.getUnreadCount()
      return count ?? 0 // Ensure we always return a number
    },
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    staleTime: 0, // Always consider data stale so invalidation causes immediate refetch
  })

  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) =>
      notificationsService.markAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({
        queryKey: ['notifications', 'unreadCount'],
      })
    },
  })

  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationsService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({
        queryKey: ['notifications', 'unreadCount'],
      })
    },
  })

  const refetchNotifications = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] })
    queryClient.invalidateQueries({
      queryKey: ['notifications', 'unreadCount'],
    })
  }

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    refetchNotifications,
  }
}
